import { Truck, Home, Building2, Heart, CheckCircle, ArrowRight, Phone, ClipboardList, PackageCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { CTABanner } from '../components/CTABanner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://panthera-umzug.de';
const typeIcons = [Home, Building2, Heart];
const stepIcons = [Phone, ClipboardList, Truck, PackageCheck];

export default function UmzugBerlinPage() {
  const { t } = useLanguage();
  const u = t.umzug;

  return (
    <>
      <SEOHead title="Umzug Berlin | Professionelles Umzugsunternehmen" description="Professioneller Umzugsservice in Berlin: Privatumzug, Firmenumzug und Seniorenumzug." canonical={`${BASE_URL}/umzug-berlin`} />

      <section className="relative pt-32 pb-20 bg-black overflow-hidden" data-testid="umzug-hero">
        <div className="absolute inset-0"><div className="absolute top-10 right-20 w-80 h-80 bg-[#D4AF37] rounded-full filter blur-[140px] opacity-15" /></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full">
              <Truck className="w-4 h-4 text-[#D4AF37]" /><span className="text-[#D4AF37] text-sm font-medium">{u.badge}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="umzug-h1">
              <span className="text-white">{u.h1_1}</span>
              <span className="bg-gradient-to-r from-[#F1D27A] via-[#D4AF37] to-[#8F6B1F] bg-clip-text text-transparent">{u.h1_2}</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-3xl mb-8">{u.subtitle}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/kontakt"><Button size="lg" className="bg-gradient-to-r from-[#F1D27A] via-[#D4AF37] to-[#8F6B1F] text-black font-bold px-8 py-6 rounded-lg hover:scale-105 transition-transform shadow-lg shadow-[#D4AF37]/30">{t.header.cta} <ArrowRight className="ml-2 w-5 h-5" /></Button></Link>
              <a href="tel:+491631945793"><Button size="lg" variant="outline" className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-bold px-8 py-6 rounded-lg hover:scale-105 transition-all"><Phone className="mr-2 w-5 h-5" /> {t.hero.cta2}</Button></a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-4 text-gray-300 leading-relaxed">
            <p>{u.intro_p1}</p><p>{u.intro_p2}</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-black" data-testid="umzug-types">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">{u.types_title}</h2>
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {u.types.map((item, i) => {
              const Icon = typeIcons[i];
              return (
                <Card key={i} className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-[#2a2a2a] hover:border-[#D4AF37]/40 transition-all duration-300" data-testid={`umzug-type-${i}`}>
                  <CardHeader>
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#8F6B1F]/20 flex items-center justify-center mb-3"><Icon className="w-7 h-7 text-[#D4AF37]" /></div>
                    <CardTitle className="text-xl text-white">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent><p className="text-gray-400 leading-relaxed">{item.desc}</p></CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0a0a0a]" data-testid="umzug-process">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">{u.process_title}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {u.steps.map((s, i) => {
              const Icon = stepIcons[i];
              return (
                <div key={i} className="relative p-6 bg-[#111] rounded-xl border border-[#2a2a2a]" data-testid={`step-${i}`}>
                  <div className="absolute -top-3 -left-3 w-8 h-8 bg-[#D4AF37] rounded-full flex items-center justify-center text-black font-bold text-sm">{i + 1}</div>
                  <Icon className="w-8 h-8 text-[#D4AF37] mb-4" />
                  <h3 className="text-white font-bold mb-2">{s.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">{u.checklist_title}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {u.checklist.map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-[#111] rounded-lg border border-[#2a2a2a]">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" /><span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABanner titleKey={u.cta_title} subtitleKey={u.cta_sub} />
    </>
  );
}
